import { useCallback, useMemo } from 'react';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';

/**
 * This hook is meant to be used in conjuction with a SearchableSelect or something similiar.
 * It will provide all the bindings for the view, and a fallback if the desired entity was not found
 */
export function useSelectEntityItems<TEntity, TValue>({
  value,
  entities,
  entityNotFoundText,
  getDisplayTextOfEntity,
  getKeyOfEntity,
  onValueChanged,
  getValueOfEntity
}: UseSelectEntityItemsOptions<
  TEntity,
  TValue
>): UseSelectEntityItemsReturnValue<TEntity> {
  const items = useItems({ entities });

  return {
    items,
    selectedItem: useSelectedItem({ value, items, getValueOfEntity }),
    getDisplayTextOfItem: useGetDisplayTextOfItem({
      entityNotFoundText,
      getDisplayTextOfEntity
    }),
    getKeyOfItem: useGetKeyOfItem({ getKeyOfEntity }),
    onSelectedItemChanged: useOnSelectedItemChanged({
      value,
      onValueChanged,
      getValueOfEntity
    })
  };
}

function useItems<TEntity>({
  entities
}: {
  entities: Array<TEntity>;
}): Array<SelectEntityItem<TEntity>> {
  return useMemo(() => {
    return entities.map<SelectEntityItem<TEntity>>((entity) => {
      return {
        type: SelectEntityItemType.Entity,
        entity
      };
    });
  }, [entities]);
}

function useSelectedItem<TEntity, TValue>({
  value,
  items,
  getValueOfEntity
}: {
  value: TValue;
  items: Array<SelectEntityItem<TEntity>>;
  getValueOfEntity: GetValueOfEntity<TEntity, TValue>;
}): SelectEntityItem<TEntity> {
  return useMemo(() => {
    const existingItem = items.find(
      (item) =>
        item.type === SelectEntityItemType.Entity &&
        getValueOfEntity(item.entity) === value
    );
    if (existingItem) {
      return existingItem;
    }

    return {
      type: SelectEntityItemType.EntityNotFound
    };
  }, [items, value, getValueOfEntity]);
}

function useGetDisplayTextOfItem<TEntity>({
  entityNotFoundText,
  getDisplayTextOfEntity
}: {
  entityNotFoundText: string;
  getDisplayTextOfEntity: GetDisplayTextOfEntity<TEntity>;
}): UseSelectEntityItemsReturnValue<TEntity>['getDisplayTextOfItem'] {
  return useCallback(
    (item) => {
      return handleItem({
        item,
        handleEntity: getDisplayTextOfEntity,
        handleNotFound: () => entityNotFoundText
      });
    },
    [getDisplayTextOfEntity, entityNotFoundText]
  );
}

function useGetKeyOfItem<TEntity>({
  getKeyOfEntity
}: {
  getKeyOfEntity: GetKeyOfEntity<TEntity>;
}): UseSelectEntityItemsReturnValue<TEntity>['getKeyOfItem'] {
  return useCallback(
    (item) => {
      return handleItem({
        item,
        handleEntity: getKeyOfEntity,
        handleNotFound: () => 'useSelectEntityItems.entityNotFound'
      });
    },
    [getKeyOfEntity]
  );
}

function useOnSelectedItemChanged<TEntity, TValue>({
  onValueChanged,
  value,
  getValueOfEntity
}: Pick<
  UseSelectEntityItemsOptions<TEntity, TValue>,
  'onValueChanged' | 'value' | 'getValueOfEntity'
>): UseSelectEntityItemsReturnValue<TEntity>['onSelectedItemChanged'] {
  return useEventCallback((item) => {
    if (item.type === SelectEntityItemType.EntityNotFound) {
      throw new Error('a entity not found item should not be selectable');
    }

    const newValue = getValueOfEntity(item.entity);

    if (newValue !== value) {
      onValueChanged(newValue);
    }
  });
}

function handleItem<TEntity, TReturnValue>({
  item,
  handleNotFound,
  handleEntity
}: {
  item: SelectEntityItem<TEntity>;
  handleNotFound: () => TReturnValue;
  handleEntity: (entity: TEntity) => TReturnValue;
}): TReturnValue {
  switch (item.type) {
    case SelectEntityItemType.Entity:
      return handleEntity(item.entity);

    case SelectEntityItemType.EntityNotFound:
      return handleNotFound();

    default:
      throw new Error(
        `item type "${(item as SelectEntityItem<TEntity>).type}" is not handled`
      );
  }
}

export type UseSelectEntityItemsOptions<TEntity, TValue> = {
  value: TValue;
  entities: Array<TEntity>;
  entityNotFoundText: string;
  onValueChanged: (value: TValue) => void;
  getDisplayTextOfEntity: GetDisplayTextOfEntity<TEntity>;
  getKeyOfEntity: GetKeyOfEntity<TEntity>;
  getValueOfEntity: GetValueOfEntity<TEntity, TValue>;
};

export type UseSelectEntityItemsReturnValue<TEntity> = {
  items: Array<SelectEntityItem<TEntity>>;
  selectedItem: SelectEntityItem<TEntity>;
  getDisplayTextOfItem: (item: SelectEntityItem<TEntity>) => string;
  getKeyOfItem: (item: SelectEntityItem<TEntity>) => string;
  onSelectedItemChanged: (item: SelectEntityItem<TEntity>) => void;
};

export type GetValueOfEntity<TEntity, TValue> = (entity: TEntity) => TValue;
export type GetDisplayTextOfEntity<TEntity> = (entity: TEntity) => string;
export type GetKeyOfEntity<TEntity> = (entity: TEntity) => string;

export enum SelectEntityItemType {
  Entity = 'entity',
  EntityNotFound = 'entityNotFound'
}

type BaseSelectEntityItem<TType extends SelectEntityItemType> = {
  type: TType;
};

export type SelectEntityEntityItem<TEntity> =
  BaseSelectEntityItem<SelectEntityItemType.Entity> & {
    entity: TEntity;
  };

export type SelectEntityEntityNotFoundItem =
  BaseSelectEntityItem<SelectEntityItemType.EntityNotFound>;

export type SelectEntityItem<TEntity> =
  | SelectEntityEntityNotFoundItem
  | SelectEntityEntityItem<TEntity>;
